import React, {useContext, useEffect, useRef, useState} from "react";
import {OperatorCountdown} from "./countdown";
import {flatIndex, inkOn, locate, sceneKey, sceneLabel, sections, sectionOf} from "../editor/describe";
import {sceneStatus, StatusGlyph} from "../editor/sceneStatus";

// Scene-metadata helpers — used by OperatorScene below and the arrow-nav
// labels in JustFuckingDance. Scene array entries can be either a bare
// React element OR a [<Element/>, {options}] tuple.

export const getSceneOpts = (entry) => {
    if (!Array.isArray(entry)) return null;
    return entry.find(item => item && typeof item === 'object' && !React.isValidElement(item)) || null;
};

export const getSceneElement = (entry) => {
    if (React.isValidElement(entry)) return entry;
    if (Array.isArray(entry)) return entry.find(item => React.isValidElement(item)) || null;
    return null;
};

export const getComponentName = (element) => {
    if (!element) return null;
    const t = element.type;
    if (typeof t === 'string') return t;
    return t?.displayName || t?.name || null;
};

export const getPropsSummary = (element) => {
    if (!element || !element.props) return '';
    const parts = [];
    for (const k of Object.keys(element.props)) {
        const v = element.props[k];
        if (k === 'children' || typeof v === 'function' || React.isValidElement(v)) continue;
        if (typeof v === 'object' && v !== null) continue;
        let s = typeof v === 'string'
            ? `"${v.length > 14 ? v.slice(0, 14) + '…' : v}"`
            : String(v);
        parts.push(`${k}=${s}`);
    }
    return parts.join(' ');
};

// The operator's phone: where the show is (folder › scene and what moves it on), the scene's own controls with its
// End button pinned above the thumb, ← and → as the scenes they go to. The title opens the jump-to list; the bar
// holds the sound check and mix, and the room's health when something's wrong. The engine's view of the room (playback, mix, health) arrives through the game
// server as opState.
const OP = {bg: '#0b0c0f', line: 'rgba(255,255,255,0.12)', dim: '#8a9099', text: '#e8eaed'};
const engineCommand = (cmd) => fetch('/operator/engine', {method: 'POST', credentials: 'include', body: JSON.stringify(cmd)}).catch(() => {});

// A scene's End button (the one that moves the show on) comes out of its controls to sit in the same place every time.
const movesOn = (c) => React.isValidElement(c) && c.type === OperatorActionButton && c.props.endpoint === '/trigger-next-scene';
const splitEnd = (el) => {
    if (!React.isValidElement(el)) return {end: null, rest: el};
    if (movesOn(el)) return {end: el, rest: null};
    if (el.type !== OperatorPanel) return {end: null, rest: el};
    let end = null;
    const kept = React.Children.toArray(el.props.children).filter((c) => {
        if (!end && movesOn(c)) { end = c; return false; }
        return true;
    });
    return {end, rest: kept.length ? React.cloneElement(el, {}, ...kept) : null};
};

// A progress count tells the pinned End button when everyone's in.
const AllInContext = React.createContext(() => {});

const useTick = (ms, on = true) => {
    const [, setN] = useState(0);
    useEffect(() => { if (!on) return; const t = setInterval(() => setN(n => n + 1), ms); return () => clearInterval(t); }, [ms, on]);
};

const HEALTH_COLOR = {ok: '#3ddc84', warn: '#ffb347', fail: '#ff5d5d'};
const MIX_BUSES = [['master', 'Master'], ['music', 'Music'], ['voice', 'Voice'], ['sfx', 'SFX']];
const smallBtn = {padding: '8px 12px', fontSize: 14, fontWeight: 700, fontFamily: 'inherit', color: 'white', background: 'rgba(255,255,255,0.08)',
    border: `1px solid ${OP.line}`, borderRadius: 8, cursor: 'pointer', touchAction: 'manipulation', WebkitTapHighlightColor: 'transparent'};

const MixPanel = ({mix}) => {
    const [local, setLocal] = useState({});
    const last = useRef({});
    const level = (bus) => local[bus] ?? (mix && mix[bus] ? mix[bus].db : 0);
    const setDb = (bus, db) => {
        setLocal(l => ({...l, [bus]: db}));
        const now = Date.now();
        if (now - (last.current[bus] || 0) > 80) { last.current[bus] = now; engineCommand({cmd: 'mix', bus, db}); }
    };
    const commit = (bus) => { engineCommand({cmd: 'mix', bus, db: level(bus)}); setLocal(l => { const {[bus]: _, ...rest} = l; return rest; }); };
    return MIX_BUSES.map(([bus, label]) => {
        const muted = !!(mix && mix[bus] && mix[bus].muted), db = level(bus);
        return <div key={bus} style={{display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', opacity: muted ? 0.5 : 1}}>
            <span style={{width: 56, fontSize: 14, fontWeight: bus === 'master' ? 700 : 400}}>{label}</span>
            <input type="range" min={-40} max={6} step={0.5} value={db} style={{flex: 1, accentColor: '#19f0a0'}}
                onChange={e => setDb(bus, +e.target.value)} onTouchEnd={() => commit(bus)} onMouseUp={() => commit(bus)}/>
            <span style={{width: 44, textAlign: 'right', fontSize: 12, fontVariantNumeric: 'tabular-nums', color: OP.dim}}>{db > 0 ? '+' : ''}{db.toFixed(1)}</span>
            <button onClick={() => engineCommand({cmd: 'mix', bus, muted: !muted})} style={{...smallBtn, background: muted ? '#ff5d5d' : smallBtn.background, color: muted ? '#000' : 'white'}}>M</button>
        </div>;
    });
};

const HealthPanel = ({health}) => health.length ? health.map(h => <div key={h.id} style={{display: 'flex', gap: 10, alignItems: 'center', padding: '6px 0', fontSize: 14}}>
    <span style={{width: 10, height: 10, borderRadius: 5, flex: 'none', background: HEALTH_COLOR[h.status] || OP.dim}}/>{h.label}
</div>) : <div style={{color: '#ff5d5d', fontSize: 14}}>The engine isn't connected.</div>;

// Every folder, colored as in the editor: tap jumps to its start, hold opens its scenes.
const Folders = ({doc, idx, jump}) => {
    const [open, setOpen] = useState(null);
    const press = useRef(null);
    const here = locate(doc, idx);
    const secs = sections(doc);
    const down = (k) => { press.current = {k, timer: setTimeout(() => { press.current = null; setOpen(o => o === k ? null : k); }, 450)}; };
    const up = (k, start) => {
        if (!press.current || press.current.k !== k) return;
        clearTimeout(press.current.timer); press.current = null;
        jump(start);
    };
    return <div style={{display: 'flex', flexDirection: 'column', gap: 10}}>
        <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8}}>
            {secs.map((sec, k) => {
                const current = here && sec.blocks.includes(here.blockIndex);
                return <button key={k} onPointerDown={() => down(k)} onPointerUp={() => up(k, flatIndex(doc, sec.blocks[0], 0))}
                    onPointerLeave={() => { if (press.current) { clearTimeout(press.current.timer); press.current = null; } }}
                    onContextMenu={(e) => e.preventDefault()}
                    style={{height: 52, padding: '0 12px', textAlign: 'left', fontSize: 15, fontWeight: 800, fontFamily: 'inherit',
                        color: inkOn(sec.color), background: sec.color, borderRadius: 8, cursor: 'pointer', touchAction: 'manipulation',
                        WebkitTapHighlightColor: 'transparent', WebkitUserSelect: 'none', userSelect: 'none',
                        border: current ? '3px solid white' : '3px solid transparent', opacity: current || !here ? 1 : 0.8,
                        overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis'}}>{sec.title}</button>;
            })}
        </div>
        {open != null && secs[open] && <div style={{display: 'flex', flexDirection: 'column', gap: 4}}>
            {secs[open].blocks.flatMap(b => doc.blocks[b].scenes.map((scene, s) => {
                const i = flatIndex(doc, b, s);
                return <button key={i} onClick={() => jump(i)} style={{...smallBtn, textAlign: 'left', fontWeight: 600,
                    borderColor: i === idx ? secs[open].color : OP.line}}>{sceneLabel(scene)}</button>;
            }))}
        </div>}
    </div>;
};

const ICONS = {
    test: <path d="M4 9v6h4l5 4V5L8 9H4zm12.5 3a4.5 4.5 0 0 0-2.5-4v8a4.5 4.5 0 0 0 2.5-4zM14 3.2v2.1a7 7 0 0 1 0 13.4v2.1a9 9 0 0 0 0-17.6z" fill="currentColor"/>,
    sound: <path d="M6 3v7M6 14v7M12 3v3M12 10v11M18 3v11M18 18v3M3.5 12h5M9.5 8h5M15.5 16h5" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"/>,
};
const barBtn = (on) => ({padding: 6, margin: -6, background: 'none', border: 'none', color: on ? 'white' : OP.dim, cursor: 'pointer',
    touchAction: 'manipulation', WebkitTapHighlightColor: 'transparent'});
// ← and → are the scenes themselves: each shows the scene it goes to.
const NavThumb = ({doc, from, to, dir, onClick}) => {
    const at = locate(doc, to), here = locate(doc, from);
    const scene = at && doc.blocks[at.blockIndex].scenes[at.sceneIndex];
    const section = at && sectionOf(doc, at.blockIndex);
    const otherFolder = section && here && sectionOf(doc, here.blockIndex).blocks[0] !== section.blocks[0];
    return <button onClick={onClick} disabled={!scene} style={{flex: 1, minWidth: 0, padding: 0, background: 'none', border: 'none', color: 'white',
        display: 'flex', flexDirection: 'column', gap: 4, cursor: scene ? 'pointer' : 'default', opacity: scene ? 1 : 0.25,
        touchAction: 'manipulation', WebkitTapHighlightColor: 'transparent', fontFamily: 'inherit'}}>
        <div style={{position: 'relative', width: '100%', aspectRatio: '16 / 9', borderRadius: 8, overflow: 'hidden',
            border: `1px solid ${otherFolder ? section.color : OP.line}`, background: scene ? `#000 url(/thumbs/${sceneKey(scene)}.jpg) center / cover no-repeat` : '#000'}}>
            <span style={{position: 'absolute', top: '50%', [dir === 'prev' ? 'left' : 'right']: 8, transform: 'translateY(-50%)', width: 34, height: 34,
                borderRadius: 17, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20}}>{dir === 'prev' ? '←' : '→'}</span>
        </div>
        <span style={{fontSize: 12, textAlign: 'center', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', color: otherFolder ? section.color : 'rgba(255,255,255,0.7)'}}>
            {scene ? (otherFolder ? `${section.title} › ${sceneLabel(scene)}` : sceneLabel(scene)) : '—'}
        </span>
    </button>;
};

// top: hangs under the title (jump to); bottom: rises above the bar (sound, health).
const Sheet = ({onClose, bottom = null, top = null, children}) => <>
    <div onClick={onClose} style={{position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 1001}}/>
    <div style={{position: 'fixed', left: 12, right: 12, zIndex: 1002, overflowY: 'auto',
        ...(top != null ? {top: `calc(${top}px + env(safe-area-inset-top))`, maxHeight: `calc(100dvh - ${top + 24}px - env(safe-area-inset-top))`}
            : {bottom: `calc(${bottom}px + env(safe-area-inset-bottom))`, maxHeight: `calc(100dvh - ${bottom + 24}px - env(safe-area-inset-bottom) - env(safe-area-inset-top))`}),
        padding: 14, borderRadius: 14, background: '#15171c', border: `1px solid ${OP.line}`, boxShadow: '0 -12px 40px rgba(0,0,0,.6)'}}>{children}</div>
</>;

const Stat = ({value, label, color = 'white'}) => <span style={{display: 'flex', flexDirection: 'column', alignItems: 'center', lineHeight: 1}}>
    <strong style={{fontSize: 17, color, fontVariantNumeric: 'tabular-nums'}}>{value}</strong>
    <span style={{fontSize: 10, letterSpacing: 1, textTransform: 'uppercase', color: OP.dim, marginTop: 3}}>{label}</span>
</span>;

export const OperatorScene = ({doc, opState, scenes, displayedSceneNumber, sendScene, sceneNumber, scene, receiveEvents, clockDrift, roundEnd}) => {
    const [readyStatus, setReadyStatus] = useState(null);
    useEffect(() => {
        let cancelled = false;
        const poll = () => {
            fetch('/users/ready-status')
                .then(r => r.ok ? r.json() : null)
                .then(s => { if (!cancelled && s) setReadyStatus(s); })
                .catch(() => {});
        };
        poll();
        const id = setInterval(poll, 3000);
        return () => { cancelled = true; clearInterval(id); };
    }, []);

    const [sheet, setSheet] = useState(null);       // 'jump' | 'sound' | 'health'
    const [allIn, setAllIn] = useState(false);
    useTick(250);

    const idx = displayedSceneNumber;
    const opts = getSceneOpts(scenes[idx]) || {};
    const live = locate(doc, idx);
    const block = live && doc.blocks[live.blockIndex];
    const docScene = block && block.scenes[live.sceneIndex];
    const section = live && sectionOf(doc, live.blockIndex);
    const status = !live ? null : opState && opState.paused ? {glyph: 'pause', color: '#ffb347', title: 'Show paused · tap to resume'}
        : sceneStatus(doc, live, opState && opState.playback, Date.now() + clockDrift);
    const {end, rest} = splitEnd(opts.operatorControls || null);
    useEffect(() => setAllIn(false), [idx]);
    const inject = (el) => el && React.cloneElement(el, {isOperator: true, timestamp: scene.timestamp, receiveEvents, clockDrift, roundEnd, sendScene, sceneNumber, scenes});
    const health = (opState && opState.health) || [];
    const worst = !opState || health.some(h => h.status === 'fail') ? 'fail' : health.some(h => h.status === 'warn') ? 'warn' : 'ok';
    // Forward is the show moving on: leaving a looping bed fades its music out first, like a scene's End button.
    const moveOn = () => fetch('/trigger-next-scene', {method: 'POST', credentials: 'include'}).catch(() => sendScene(idx + 1));
    // The glyph pauses and resumes the whole show: music, Zed, the games' timers and countdowns.
    const paused = !!(opState && opState.paused);
    const togglePlay = () => engineCommand({cmd: paused ? 'resume-show' : 'pause-show'});
    const NAV = 135, STATUS = 56, BAR = NAV + STATUS, bottom = end ? BAR + 84 : BAR;

    return <div style={{
        alignSelf: 'flex-start', width: '100%', minHeight: '100dvh', boxSizing: 'border-box',
        padding: `calc(16px + env(safe-area-inset-top)) 16px calc(${bottom + 20}px + env(safe-area-inset-bottom))`,
        display: 'flex', flexDirection: 'column', gap: 16, backgroundColor: OP.bg, color: 'white', fontFamily: 'inherit',
    }}>
        <style>{'@keyframes op-pulse { 0%, 100% { transform: scale(1); filter: brightness(1); } 50% { transform: scale(1.03); filter: brightness(1.35); } }'}</style>
        <div style={{display: 'flex', flexDirection: 'column', gap: 8}}>
            <button onClick={() => setSheet(s => s === 'jump' ? null : 'jump')} style={{fontSize: 22, fontWeight: 800, letterSpacing: -0.3, lineHeight: 1.15,
                textAlign: 'center', color: 'white', background: 'none', border: 'none', padding: 0, fontFamily: 'inherit', cursor: 'pointer',
                position: 'relative', zIndex: 1003, touchAction: 'manipulation', WebkitTapHighlightColor: 'transparent'}}>
                {section && <span style={{color: section.color}}>{section.title}</span>}
                {section && <span style={{color: OP.dim, fontWeight: 500, margin: '0 8px'}}>›</span>}
                <span>{docScene ? sceneLabel(docScene) : `#${idx}`}</span>
                <span style={{color: OP.dim, fontSize: 16, marginLeft: 8}}>▾</span>
            </button>
            {readyStatus && readyStatus.not_ready && readyStatus.not_ready.length > 0 && <div style={{fontSize: 12, opacity: 0.6, textAlign: 'right',
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}>missing: {readyStatus.not_ready.join(', ')}</div>}
        </div>

        <AllInContext.Provider value={setAllIn}>{rest && inject(rest)}</AllInContext.Provider>

        <div style={{position: 'fixed', left: 0, right: 0, bottom: 0, height: `calc(${bottom}px + env(safe-area-inset-bottom))`, backgroundColor: OP.bg,
            borderTop: `1px solid ${OP.line}`, zIndex: 999}}/>
        {end && <div style={{position: 'fixed', left: 16, right: 16, bottom: `calc(${BAR}px + env(safe-area-inset-bottom))`, zIndex: 1000,
            animation: allIn ? 'op-pulse 1.1s ease-in-out infinite' : 'none'}}>
            {React.cloneElement(end, {color: (section && section.color) || '#ffd24a'})}
        </div>}
        <div style={{position: 'fixed', left: 16, right: 16, bottom: `calc(${NAV + 4}px + env(safe-area-inset-bottom))`, height: 44,
            display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, zIndex: 1003}}>
            <span style={{display: 'flex', alignItems: 'center', gap: 12}}>
                {status && <button onClick={togglePlay} style={{padding: '4px 8px', margin: '0 -8px', background: 'none', border: 'none', cursor: 'pointer',
                    touchAction: 'manipulation', WebkitTapHighlightColor: 'transparent', fontFamily: 'inherit'}}><StatusGlyph status={status} size={26} fontSize={20}/></button>}
                <span style={{fontSize: 18, lineHeight: 1}}><OperatorCountdown roundEnd={roundEnd} clockDrift={clockDrift}/></span>
            </span>
            <span style={{display: 'flex', alignItems: 'center', gap: 18, whiteSpace: 'nowrap'}}>
                {worst !== 'ok' && <button onClick={() => setSheet(s => s === 'health' ? null : 'health')} style={barBtn(sheet === 'health')}>
                    <span style={{display: 'block', width: 14, height: 14, borderRadius: 7, background: HEALTH_COLOR[worst]}}/>
                </button>}
                <button onClick={() => setSheet(s => s === 'sound' ? null : 'sound')} style={barBtn(sheet === 'sound')}>
                    <svg width="24" height="24" viewBox="0 0 24 24" style={{display: 'block'}}>{ICONS.sound}</svg>
                </button>
                {readyStatus && <Stat value={readyStatus.active ?? '—'} label="active" color="#9bf0a0"/>}
                {readyStatus && <Stat value={`${readyStatus.ready}/${readyStatus.total}`} label="ready"/>}
            </span>
        </div>
        <div style={{position: 'fixed', left: 16, right: 16, bottom: 'calc(14px + env(safe-area-inset-bottom))', display: 'flex', gap: 12, zIndex: 1000}}>
            <NavThumb doc={doc} from={idx} to={idx - 1} dir="prev" onClick={() => idx > 0 && sendScene(idx - 1)}/>
            <NavThumb doc={doc} from={idx} to={idx + 1} dir="next" onClick={moveOn}/>
        </div>
        {sheet === 'jump' && <Sheet top={64} onClose={() => setSheet(null)}>
            <Folders doc={doc} idx={idx} jump={(i) => { sendScene(i); setSheet(null); }}/>
        </Sheet>}
        {sheet === 'sound' && <Sheet bottom={bottom + 6} onClose={() => setSheet(null)}>
            <button onClick={() => engineCommand({cmd: 'test-beep'})} disabled={!opState} style={{...smallBtn, width: '100%', display: 'flex', alignItems: 'center',
                justifyContent: 'center', gap: 8, marginBottom: 8}}>
                <svg width="18" height="18" viewBox="0 0 24 24">{ICONS.test}</svg>Test beeps
            </button>
            <MixPanel mix={opState && opState.mix}/>
        </Sheet>}
        {sheet === 'health' && <Sheet bottom={bottom + 6} onClose={() => setSheet(null)}>
            <HealthPanel health={opState ? health : []}/>
        </Sheet>}
    </div>;
};

// Operator-side button that hits a given endpoint and shows status. Drop
// into a scene's `operatorControls` slot:
//
//   [<InstructionsScene lines={['GET READY: TRILEMMA']}/>, {
//     name: 'get ready trilemma',
//     operatorControls: <OperatorInitButton label="Init Trilemma" endpoint="/trilemma/init"/>,
//   }]
//
// Status flow: idle → 🕚 (sending) → ✅ ok | ❌ failed.
export const OperatorInitButton = ({label, endpoint, method = 'GET', confirm = false}) => {
    const [status, setStatus] = useState('idle'); // 'idle' | 'pending' | 'ok' | 'error'

    const onClick = () => {
        if (confirm && !window.confirm(`${label}?\n\nThis will reset state for that game.`)) return;
        setStatus('pending');
        fetch(endpoint, {method, credentials: 'include'})
            .then((r) => setStatus(r.ok ? 'ok' : 'error'))
            .catch(() => setStatus('error'));
    };

    const colors = {
        idle: {bg: 'linear-gradient(180deg, #1a1a22 0%, #08080d 100%)', border: 'rgba(255,255,255,0.55)'},
        pending: {bg: 'linear-gradient(180deg, #2a2a32 0%, #14141a 100%)', border: '#ffd24a'},
        ok: {bg: 'linear-gradient(180deg, #0a4520 0%, #042010 100%)', border: '#9bf0a0'},
        error: {bg: 'linear-gradient(180deg, #4a0a0a 0%, #200404 100%)', border: '#ff6a6a'},
    };
    const c = colors[status];
    const icon = {idle: '', pending: '🕚 ', ok: '✅ ', error: '❌ '}[status];

    return <button
        onClick={onClick}
        disabled={status === 'pending'}
        style={{
            width: '100%',
            padding: '14px 20px',
            fontSize: 17,
            fontWeight: 800,
            fontStyle: 'italic',
            letterSpacing: 4,
            textTransform: 'uppercase',
            fontFamily: 'inherit',
            color: 'white',
            background: c.bg,
            border: `1px solid ${c.border}`,
            borderRadius: 8,
            boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.18), 0 2px 0 rgba(0,0,0,0.6)',
            cursor: status === 'pending' ? 'wait' : 'pointer',
            touchAction: 'manipulation',
            WebkitTapHighlightColor: 'transparent',
            opacity: status === 'pending' ? 0.7 : 1,
            transition: 'background 120ms, border-color 120ms, opacity 120ms',
        }}>{icon}{label}</button>;
};

// Stack multiple controls vertically with a small gap. Forwards any
// injected props (sendScene, sceneNumber, scenes, isOperator, etc.) from
// the operator render layer down to each child so wrapped buttons work.
export const OperatorPanel = ({children, ...injected}) => (
    <div style={{display: 'flex', flexDirection: 'column', gap: 10}}>
        {React.Children.map(children, (child) =>
            React.isValidElement(child) ? React.cloneElement(child, injected) : child
        )}
    </div>
);

// Generic progress counter for the operator. Polls a status endpoint
// every 2s and surfaces submitted/expected as a big fraction. If a
// pendingKey is provided and the response contains an array of names,
// they're listed below as coral chips.
//
//   <OperatorProgressCount label="Dances submitted" endpoint="/dancelestrations/get-submission-counts" countKey="dance_gifs" pendingKey="dance_gifs_pending"/>
export const OperatorProgressCount = ({label, endpoint, countKey, expectedKey = 'expected', pendingKey}) => {
    const [data, setData] = useState(null);
    const reportAllIn = useContext(AllInContext);
    const everyoneIn = !!data && (data[expectedKey] || 0) > 0 && (data[countKey] || 0) >= (data[expectedKey] || 0);
    useEffect(() => { reportAllIn(everyoneIn); }, [everyoneIn, reportAllIn]);
    useEffect(() => {
        let cancelled = false;
        const poll = () => {
            fetch(endpoint)
                .then((r) => r.ok ? r.json() : null)
                .then((d) => { if (!cancelled && d) setData(d); })
                .catch(() => {});
        };
        poll();
        const id = setInterval(poll, 2000);
        return () => { cancelled = true; clearInterval(id); };
    }, [endpoint]);

    if (!data) return <div style={{opacity: 0.5, fontStyle: 'italic', textAlign: 'center', padding: 20}}>loading…</div>;

    const count = data[countKey] || 0;
    const expected = data[expectedKey] || 0;
    const pending = pendingKey ? (data[pendingKey] || []) : null;
    const allDone = expected > 0 && count >= expected;
    const color = allDone ? '#9bf0a0' : count > 0 ? '#ffd24a' : 'white';

    return <div style={{display: 'flex', flexDirection: 'column', gap: 12}}>
        <div style={{
            padding: '20px 16px',
            borderRadius: 8,
            backgroundColor: 'rgba(255,255,255,0.05)',
            border: '1px solid rgba(255,255,255,0.18)',
            textAlign: 'center',
        }}>
            <div style={{
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: 3,
                textTransform: 'uppercase',
                opacity: 0.7,
                marginBottom: 8,
            }}>{label}</div>
            <div style={{
                fontSize: 44,
                fontWeight: 900,
                fontStyle: 'italic',
                lineHeight: 1,
                color,
                fontVariantNumeric: 'tabular-nums',
                textShadow: '2px 2px 0 rgba(0,0,0,0.85)',
            }}>{count} <span style={{opacity: 0.5, fontSize: 28}}>/ {expected}</span></div>
        </div>
        {pending !== null && (
            <div style={{
                padding: 14,
                borderRadius: 8,
                backgroundColor: 'rgba(255,255,255,0.04)',
                border: '1px dashed rgba(255,255,255,0.2)',
            }}>
                <div style={{
                    fontSize: 11,
                    fontWeight: 700,
                    letterSpacing: 3,
                    textTransform: 'uppercase',
                    opacity: 0.7,
                    marginBottom: 8,
                }}>Pending</div>
                {pending.length === 0 ? (
                    <div style={{
                        fontSize: 16,
                        fontStyle: 'italic',
                        color: '#9bf0a0',
                        textAlign: 'center',
                        padding: '4px 0',
                    }}>✨ all submitted</div>
                ) : (
                    <div style={{display: 'flex', flexWrap: 'wrap', gap: 6}}>
                        {pending.map((name) => (
                            <span key={name} style={{
                                padding: '6px 10px',
                                fontSize: 14,
                                fontWeight: 600,
                                backgroundColor: 'rgba(255,106,106,0.15)',
                                border: '1px solid rgba(255,106,106,0.4)',
                                borderRadius: 6,
                                color: '#ff9b9b',
                            }}>{name}</span>
                        ))}
                    </div>
                )}
            </div>
        )}
    </div>;
};

// Two-input form for posting a manual fake battle. Hits POST
// /trilemma/fake-battle with {challenger, challengee, gameNumber}.
export const OperatorFakeBattleForm = ({gameNumber}) => {
    const [challenger, setChallenger] = useState('');
    const [challengee, setChallengee] = useState('');
    const [status, setStatus] = useState('idle');

    const submit = () => {
        if (!challenger.trim() || !challengee.trim()) return;
        setStatus('pending');
        fetch('/trilemma/fake-battle', {
            method: 'POST',
            credentials: 'include',
            headers: {'Content-Type': 'application/json'},
            body: JSON.stringify({challenger: challenger.trim(), challengee: challengee.trim(), gameNumber}),
        })
            .then((r) => {
                if (r.ok) {
                    setStatus('ok');
                    setChallenger('');
                    setChallengee('');
                } else setStatus('error');
            })
            .catch(() => setStatus('error'));
        setTimeout(() => setStatus('idle'), 1500);
    };

    const inputStyle = {
        flex: 1,
        minWidth: 0,
        padding: '10px 12px',
        fontSize: 16,
        fontFamily: 'inherit',
        background: 'rgba(0,0,0,0.5)',
        color: 'white',
        border: '1px solid rgba(255,255,255,0.3)',
        borderRadius: 6,
        outline: 'none',
    };
    const icon = {idle: '', pending: '🕚 ', ok: '✅ ', error: '❌ '}[status];

    return <div style={{
        padding: 12,
        borderRadius: 8,
        backgroundColor: 'rgba(255,255,255,0.04)',
        border: '1px dashed rgba(255,255,255,0.2)',
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
    }}>
        <div style={{
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: 3,
            textTransform: 'uppercase',
            opacity: 0.7,
            textAlign: 'center',
        }}>Fake Battle</div>
        <div style={{display: 'flex', gap: 8}}>
            <input style={inputStyle} placeholder="Challenger" value={challenger} onChange={(e) => setChallenger(e.target.value)}/>
            <span style={{alignSelf: 'center', opacity: 0.5}}>vs</span>
            <input style={inputStyle} placeholder="Challengee" value={challengee} onChange={(e) => setChallengee(e.target.value)}/>
        </div>
        <button
            onClick={submit}
            disabled={status === 'pending' || !challenger.trim() || !challengee.trim()}
            style={{
                padding: '10px',
                fontSize: 15,
                fontWeight: 800,
                letterSpacing: 2,
                textTransform: 'uppercase',
                fontFamily: 'inherit',
                color: 'white',
                background: 'linear-gradient(180deg, #1a1a22 0%, #08080d 100%)',
                border: '1px solid rgba(255,255,255,0.4)',
                borderRadius: 6,
                cursor: 'pointer',
                touchAction: 'manipulation',
                WebkitTapHighlightColor: 'transparent',
                opacity: (!challenger.trim() || !challengee.trim()) ? 0.4 : 1,
            }}>{icon}Submit</button>
    </div>;
};

// Live snapshot of the current Trilemma game — players in battle, remaining,
// eliminated. Polls /trilemma/get-game-stats?game_number=N every 2s.
export const OperatorTrilemmaStats = ({gameNumber, melee = false}) => {
    const [data, setData] = useState(null);
    useEffect(() => {
        let cancelled = false;
        const poll = () => {
            fetch(`/trilemma/get-game-stats?game_number=${gameNumber}`)
                .then((r) => r.ok ? r.json() : null)
                .then((d) => { if (!cancelled && d) setData(d); })
                .catch(() => {});
        };
        poll();
        const id = setInterval(poll, 2000);
        return () => { cancelled = true; clearInterval(id); };
    }, [gameNumber]);

    if (!data) return <div style={{opacity: 0.5, fontStyle: 'italic', textAlign: 'center', padding: 20}}>loading…</div>;

    const block = (label, value, color) => (
        <div style={{
            flex: 1,
            padding: '14px 8px',
            borderRadius: 8,
            backgroundColor: 'rgba(255,255,255,0.05)',
            border: '1px solid rgba(255,255,255,0.15)',
            textAlign: 'center',
            minWidth: 0,
        }}>
            <div style={{
                fontSize: 32,
                fontWeight: 900,
                fontStyle: 'italic',
                lineHeight: 1,
                color,
                fontVariantNumeric: 'tabular-nums',
                textShadow: '2px 2px 0 rgba(0,0,0,0.85)',
            }}>{value}</div>
            <div style={{
                fontSize: 10,
                fontWeight: 700,
                letterSpacing: 2,
                textTransform: 'uppercase',
                opacity: 0.7,
                marginTop: 4,
                lineHeight: 1.1,
            }}>{label}</div>
        </div>
    );

    return <div style={{display: 'flex', flexDirection: 'column', gap: 8}}>
        <div style={{
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: 3,
            textTransform: 'uppercase',
            opacity: 0.7,
            textAlign: 'center',
        }}>{melee ? `Trilemma Melee${data.melee_closed ? ' · LAST CALL' : ''}` : `Trilemma Game ${gameNumber}`}</div>
        <div style={{display: 'flex', gap: 8}}>
            {melee ? <>
                {block('Total', data.total_players, 'white')}
                {block('In Battle', data.in_battle, '#9be0ff')}
                {block('Live Duels', data.active_battles, data.melee_closed && data.active_battles > 0 ? '#ffd24a' : '#9bf0a0')}
                {block('Finished', data.battles_finished, 'white')}
            </> : <>
                {block('Total', data.total_players, 'white')}
                {block('Remain', data.remaining, '#9bf0a0')}
                {block('In Battle', data.in_battle, '#9be0ff')}
                {block('Out', data.eliminated, '#ff9b9b')}
            </>}
        </div>
        {!melee && <div style={{
            fontSize: 12,
            opacity: 0.6,
            textAlign: 'center',
        }}>{data.active_battles} active battles</div>}
    </div>;
};

// Show a Make It A Dance assignment for a given round on the operator
// panel — who's dancing and what the prompt is. If `token` is set,
// fetches that specific player's assignment (used in rainbow round 8
// where everyone has their own); otherwise fetches the spotlight one
// for the round.
export const OperatorMakeItADanceAssignment = ({round, token}) => {
    const [data, setData] = useState(null);
    useEffect(() => {
        let cancelled = false;
        const url = token
            ? `/make-it-a-dance/get-self-assignment?round=${round}&token=${token}`
            : `/make-it-a-dance/get-spotlight-round-assignment?round=${round}`;
        fetch(url)
            .then((r) => r.ok ? r.json() : null)
            .then((d) => { if (!cancelled && d) setData(d); })
            .catch(() => {});
        return () => { cancelled = true; };
    }, [round, token]);

    if (!data) return <div style={{opacity: 0.5, fontStyle: 'italic', textAlign: 'center', padding: 20}}>loading…</div>;

    const c = data.character || {};
    return <div style={{
        padding: '16px 14px',
        borderRadius: 8,
        backgroundColor: 'rgba(255,255,255,0.05)',
        border: '1px solid rgba(255,255,255,0.18)',
        display: 'flex',
        gap: 12,
        alignItems: 'center',
    }}>
        {c.image_url && (
            <img
                src={c.image_url}
                alt={c.name}
                style={{
                    width: 64,
                    height: 64,
                    borderRadius: 6,
                    objectFit: 'cover',
                    objectPosition: 'top',
                    flexShrink: 0,
                    filter: 'drop-shadow(0 0 1px black) drop-shadow(0 0 1px black)',
                }}
            />
        )}
        <div style={{flex: 1, minWidth: 0}}>
            <div style={{
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: 3,
                textTransform: 'uppercase',
                opacity: 0.7,
                marginBottom: 2,
            }}>{c.name || '—'}{c.nickname ? ` · ${c.nickname}` : ''}</div>
            <div style={{
                fontSize: 18,
                fontWeight: 800,
                fontStyle: 'italic',
                lineHeight: 1.2,
                color: '#ffd24a',
                textShadow: '1px 1px 0 rgba(0,0,0,0.85)',
            }}>"{data.value}"</div>
        </div>
    </div>;
};

// Operator panel for Allegedly True: current phase, vote tally, list of
// pending voters, and Next/Back advance buttons.
export const OperatorAllegedlyTrueStatus = () => {
    const [state, setState] = useState(null);

    const fetchState = () => {
        fetch('/allegedly-true/state', {credentials: 'include'})
            .then((r) => r.ok ? r.json() : null)
            .then((s) => { if (s) setState(s); })
            .catch(() => {});
    };

    useEffect(() => {
        let cancelled = false;
        const poll = () => { if (!cancelled) fetchState(); };
        poll();
        const id = setInterval(poll, 1500);
        return () => { cancelled = true; clearInterval(id); };
    }, []);

    const advance = (direction) => {
        fetch('/allegedly-true/advance', {
            method: 'POST',
            credentials: 'include',
            headers: {'Content-Type': 'application/json'},
            body: JSON.stringify({direction}),
        }).then(() => fetchState()).catch(() => {});
    };
    const toggleAuto = () => {
        fetch('/allegedly-true/auto', {
            method: 'POST',
            credentials: 'include',
            headers: {'Content-Type': 'application/json'},
            body: JSON.stringify({auto: !(state && state.auto)}),
        }).then(() => fetchState()).catch(() => {});
    };

    if (!state) return <div style={{opacity: 0.5, fontStyle: 'italic', textAlign: 'center', padding: 20}}>loading…</div>;

    const phase = state.currentPhase || 'transition';
    const tally = state.currentTally || {count: 0, total: 0};
    const pending = state.pendingVoters || [];
    const factsOnly = (state.facts || []).filter(f => f.display_as !== 'narration');
    const factPos = factsOnly.findIndex(f => f.id === state.currentFactId);

    const phaseColor = phase === 'voting' ? '#ffd24a' : phase === 'revealed' ? '#9bf0a0' : '#9be0ff';

    const navBtn = (label, dir) => (
        <button
            onClick={() => advance(dir)}
            style={{
                flex: 1,
                padding: '14px 12px',
                fontSize: 16,
                fontWeight: 800,
                fontStyle: 'italic',
                letterSpacing: 3,
                textTransform: 'uppercase',
                fontFamily: 'inherit',
                color: 'white',
                background: 'linear-gradient(180deg, #2a2a32 0%, #0a0a14 100%)',
                border: '1px solid rgba(255,255,255,0.55)',
                borderRadius: 8,
                boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.18), 0 2px 0 rgba(0,0,0,0.6)',
                cursor: 'pointer',
                touchAction: 'manipulation',
                WebkitTapHighlightColor: 'transparent',
            }}>{label}</button>
    );

    return <div style={{display: 'flex', flexDirection: 'column', gap: 12}}>
        <div style={{
            padding: '14px 16px',
            borderRadius: 8,
            backgroundColor: 'rgba(255,255,255,0.05)',
            border: '1px solid rgba(255,255,255,0.18)',
            textAlign: 'center',
        }}>
            <div style={{fontSize: 11, fontWeight: 700, letterSpacing: 3, textTransform: 'uppercase', opacity: 0.7, marginBottom: 4}}>Phase</div>
            <div style={{
                fontSize: 26,
                fontWeight: 900,
                fontStyle: 'italic',
                letterSpacing: 2,
                textTransform: 'uppercase',
                color: phaseColor,
                textShadow: '2px 2px 0 rgba(0,0,0,0.85)',
            }}>{phase}</div>
            {factsOnly.length > 0 && factPos >= 0 && (
                <div style={{fontSize: 13, opacity: 0.7, marginTop: 4}}>
                    Fact {factPos + 1} / {factsOnly.length}
                </div>
            )}
        </div>

        <div style={{
            padding: '16px 12px',
            borderRadius: 8,
            backgroundColor: 'rgba(255,255,255,0.05)',
            border: '1px solid rgba(255,255,255,0.18)',
            textAlign: 'center',
        }}>
            <div style={{fontSize: 11, fontWeight: 700, letterSpacing: 3, textTransform: 'uppercase', opacity: 0.7, marginBottom: 6}}>Votes Cast</div>
            <div style={{
                fontSize: 36,
                fontWeight: 900,
                fontStyle: 'italic',
                lineHeight: 1,
                color: 'white',
                fontVariantNumeric: 'tabular-nums',
                textShadow: '2px 2px 0 rgba(0,0,0,0.85)',
            }}>{tally.count} <span style={{opacity: 0.5, fontSize: 22}}>/ {tally.total}</span></div>
            {phase === 'revealed' && tally.true !== null && tally.true !== undefined && (
                <div style={{fontSize: 13, opacity: 0.85, marginTop: 6}}>
                    <span style={{color: '#9bf0a0'}}>True: {tally.true}</span>
                    {' · '}
                    <span style={{color: '#ff9b9b'}}>False: {tally.false}</span>
                </div>
            )}
        </div>

        <div style={{
            padding: 12,
            borderRadius: 8,
            backgroundColor: 'rgba(255,255,255,0.04)',
            border: '1px dashed rgba(255,255,255,0.2)',
        }}>
            <div style={{fontSize: 11, fontWeight: 700, letterSpacing: 3, textTransform: 'uppercase', opacity: 0.7, marginBottom: 8}}>Hasn't Voted</div>
            {pending.length === 0 ? (
                <div style={{fontSize: 14, fontStyle: 'italic', color: '#9bf0a0', textAlign: 'center'}}>✨ everyone voted</div>
            ) : (
                <div style={{display: 'flex', flexWrap: 'wrap', gap: 6}}>
                    {pending.map((name) => (
                        <span key={name} style={{
                            padding: '4px 8px',
                            fontSize: 13,
                            fontWeight: 600,
                            backgroundColor: 'rgba(255,106,106,0.15)',
                            border: '1px solid rgba(255,106,106,0.4)',
                            borderRadius: 6,
                            color: '#ff9b9b',
                        }}>{name}</span>
                    ))}
                </div>
            )}
        </div>

        <div style={{display: 'flex', gap: 8}}>
            {navBtn('← Back', 'back')}
            {navBtn('Next →', 'next')}
            <button onClick={toggleAuto} title={state.auto ? 'Moving on by itself. Back switches to manual.' : 'Waiting for Next.'} style={{
                padding: '14px 12px', fontSize: 14, fontWeight: 800, letterSpacing: 2, fontFamily: 'inherit', borderRadius: 4,
                background: '#0a0a14', cursor: 'pointer',
                color: state.auto ? '#36ff36' : '#ffd24a', border: `1px solid ${state.auto ? '#36ff36' : '#ffd24a'}`,
            }}>{state.auto ? 'AUTO ⏵' : 'MANUAL ⏸'}</button>
        </div>
    </div>;
};

// Detailed ready-status panel for the operator. Polls /users/ready-status
// every 2s and surfaces big counts + the list of who's not ready yet.
export const OperatorReadyStatus = () => {
    const [status, setStatus] = useState(null);
    useEffect(() => {
        let cancelled = false;
        const poll = () => {
            fetch('/users/ready-status')
                .then((r) => r.ok ? r.json() : null)
                .then((s) => { if (!cancelled && s) setStatus(s); })
                .catch(() => {});
        };
        poll();
        const id = setInterval(poll, 2000);
        return () => { cancelled = true; clearInterval(id); };
    }, []);

    if (!status) {
        return <div style={{opacity: 0.5, fontStyle: 'italic', textAlign: 'center', padding: 20}}>
            loading…
        </div>;
    }

    const {ready = 0, total = 0, active = 0, not_ready = []} = status;
    const allReady = ready === total && total > 0;

    const statBlock = (label, value, color) => (
        <div style={{
            flex: 1,
            padding: '14px 12px',
            borderRadius: 8,
            backgroundColor: 'rgba(255,255,255,0.05)',
            border: '1px solid rgba(255,255,255,0.15)',
            textAlign: 'center',
        }}>
            <div style={{
                fontSize: 36,
                fontWeight: 900,
                fontStyle: 'italic',
                lineHeight: 1,
                color,
                fontVariantNumeric: 'tabular-nums',
                textShadow: '2px 2px 0 rgba(0,0,0,0.85)',
            }}>{value}</div>
            <div style={{
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: 2,
                textTransform: 'uppercase',
                opacity: 0.7,
                marginTop: 4,
            }}>{label}</div>
        </div>
    );

    return <div style={{display: 'flex', flexDirection: 'column', gap: 12}}>
        <div style={{display: 'flex', gap: 8}}>
            {statBlock('Total', total, 'white')}
            {statBlock('Ready', ready, allReady ? '#9bf0a0' : '#ffd24a')}
            {statBlock('Active', active, '#9be0ff')}
        </div>

        <div style={{
            padding: 14,
            borderRadius: 8,
            backgroundColor: 'rgba(255,255,255,0.04)',
            border: '1px dashed rgba(255,255,255,0.2)',
            minHeight: 60,
        }}>
            <div style={{
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: 3,
                textTransform: 'uppercase',
                opacity: 0.7,
                marginBottom: 8,
            }}>Not Ready</div>
            {not_ready.length === 0 ? (
                <div style={{
                    fontSize: 16,
                    fontStyle: 'italic',
                    color: '#9bf0a0',
                    textAlign: 'center',
                    padding: '8px 0',
                }}>✨ everyone's ready</div>
            ) : (
                <div style={{display: 'flex', flexWrap: 'wrap', gap: 6}}>
                    {not_ready.map((name) => (
                        <span key={name} style={{
                            padding: '6px 10px',
                            fontSize: 14,
                            fontWeight: 600,
                            backgroundColor: 'rgba(255,106,106,0.15)',
                            border: '1px solid rgba(255,106,106,0.4)',
                            borderRadius: 6,
                            color: '#ff9b9b',
                        }}>{name}</span>
                    ))}
                </div>
            )}
        </div>
    </div>;
};

// Jump to a named scene by its options.name. Useful for "Start Game 2"
// type buttons that skip past the score recap into the next game.
// `sendScene` and `scenes` are injected by the operator render layer.
// Green styling — semantically a "go" / start action.
export const OperatorJumpToSceneButton = ({label, target, sendScene, scenes}) => {
    const onClick = () => {
        if (typeof sendScene !== 'function' || !Array.isArray(scenes)) return;
        const idx = scenes.findIndex((s) =>
            Array.isArray(s) &&
            s.find((item) => item && typeof item === 'object' && !item.props && item.name === target)
        );
        if (idx >= 0) sendScene(idx);
        else console.warn('[OperatorJumpToSceneButton] no scene named', target);
    };
    return <button
        onClick={onClick}
        style={{
            width: '100%',
            padding: '20px 24px',
            fontSize: 22,
            fontWeight: 900,
            fontStyle: 'italic',
            letterSpacing: 5,
            textTransform: 'uppercase',
            fontFamily: 'inherit',
            color: 'white',
            background: 'linear-gradient(180deg, #0aff35 0%, #045018 100%)',
            border: '2px solid #9bf0a0',
            borderRadius: 10,
            boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.35), 0 4px 0 rgba(0,0,0,0.6), 0 0 24px rgba(155,240,160,0.4)',
            cursor: 'pointer',
            touchAction: 'manipulation',
            WebkitTapHighlightColor: 'transparent',
            WebkitAppearance: 'none',
            textShadow: '1px 1px 0 rgba(0,0,0,0.85)',
        }}>{label}</button>;
};

// Advances the JFD scene index directly via the existing arrow-button code
// path. Doesn't trigger Max. Yellow/gold styling — semantically an "end"
// action that closes a game/round.
// `sendScene` and `sceneNumber` are injected by the operator render layer.
export const OperatorNextSceneButton = ({label, sendScene, sceneNumber}) => {
    const onClick = () => {
        if (typeof sendScene === 'function') sendScene((sceneNumber ?? 0) + 1);
    };
    return <button
        onClick={onClick}
        style={{
            width: '100%',
            padding: '20px 24px',
            fontSize: 22,
            fontWeight: 900,
            fontStyle: 'italic',
            letterSpacing: 5,
            textTransform: 'uppercase',
            fontFamily: 'inherit',
            color: 'white',
            background: 'linear-gradient(180deg, #ffd24a 0%, #8a6a00 100%)',
            border: '2px solid #ffd24a',
            borderRadius: 10,
            boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.35), 0 4px 0 rgba(0,0,0,0.6), 0 0 24px rgba(255,210,74,0.4)',
            cursor: 'pointer',
            touchAction: 'manipulation',
            WebkitTapHighlightColor: 'transparent',
            WebkitAppearance: 'none',
            textShadow: '1px 1px 0 rgba(0,0,0,0.85)',
        }}>{label}</button>;
};

// Big prominent action button. Variant 'start' (green, default) for "go"
// actions; variant 'end' (gold/yellow) for closing actions. Hits an HTTP
// endpoint (typically /trigger-next-scene to fire Max).
// color: the pinned End button wears its folder's color.
export const OperatorActionButton = ({label, endpoint, method = 'POST', variant = 'start', color = null}) => {
    const [status, setStatus] = useState('idle');
    const onClick = () => {
        setStatus('pending');
        fetch(endpoint, {method, credentials: 'include'})
            .then((r) => setStatus(r.ok ? 'ok' : 'error'))
            .catch(() => setStatus('error'));
        // Flash ok back to idle so it can be re-fired.
        setTimeout(() => setStatus('idle'), 1500);
    };
    const palette = color
        ? {bg: color, ring: color, ink: inkOn(color)}
        : variant === 'end'
        ? {bg: 'linear-gradient(180deg, #ffd24a 0%, #8a6a00 100%)', ring: '#ffd24a'}
        : {bg: 'linear-gradient(180deg, #0aff35 0%, #045018 100%)', ring: '#9bf0a0'};
    const ringColor = status === 'pending' ? '#ffd24a' : status === 'error' ? '#ff6a6a' : palette.ring;
    return <button
        onClick={onClick}
        disabled={status === 'pending'}
        style={{
            width: '100%',
            padding: '20px 24px',
            fontSize: 22,
            fontWeight: 900,
            fontStyle: 'italic',
            letterSpacing: 5,
            textTransform: 'uppercase',
            fontFamily: 'inherit',
            color: palette.ink || 'white',
            background: palette.bg,
            border: `2px solid ${ringColor}`,
            borderRadius: 10,
            boxShadow: `inset 0 1px 0 rgba(255,255,255,0.35), 0 4px 0 rgba(0,0,0,0.6), 0 0 24px ${ringColor}66`,
            cursor: status === 'pending' ? 'wait' : 'pointer',
            touchAction: 'manipulation',
            WebkitTapHighlightColor: 'transparent',
            textShadow: palette.ink === '#0b0c0f' ? 'none' : '1px 1px 0 rgba(0,0,0,0.85)',
            transition: 'border-color 120ms, box-shadow 120ms, background 120ms',
        }}>{label}</button>;
};

// Batched init panel: renders one row per target plus an "Init All" button
// at the bottom that fires every target in parallel. Each row shows its
// own status (idle / 🕚 / ✅ / ❌) like the init page.
//
// Usage:
//   <OperatorInitGroup targets={[
//     {label: 'Dancelestrations', endpoint: '/dancelestrations/init'},
//     {label: 'Trilemma', endpoint: '/trilemma/init'},
//     ...
//   ]}/>
export const OperatorInitGroup = ({targets = []}) => {
    const [statuses, setStatuses] = useState(() => targets.map(() => 'idle'));

    const fireOne = (i) => {
        setStatuses((prev) => prev.map((s, idx) => idx === i ? 'pending' : s));
        const {endpoint, method = 'GET'} = targets[i];
        return fetch(endpoint, {method, credentials: 'include'})
            .then((r) => {
                setStatuses((prev) => prev.map((s, idx) => idx === i ? (r.ok ? 'ok' : 'error') : s));
                return r.ok;
            })
            .catch(() => {
                setStatuses((prev) => prev.map((s, idx) => idx === i ? 'error' : s));
                return false;
            });
    };

    const fireAll = () => {
        if (!window.confirm(`Init ALL ${targets.length} games?\n\nThis will reset state for every game.`)) return;
        targets.forEach((_, i) => fireOne(i));
    };

    const icon = (s) => ({idle: '⚪', pending: '🕚', ok: '✅', error: '❌'}[s] || '⚪');

    const rowBtnStyle = {
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        width: '100%',
        padding: '12px 16px',
        fontSize: 16,
        fontWeight: 700,
        fontFamily: 'inherit',
        textAlign: 'left',
        color: 'white',
        background: 'rgba(255,255,255,0.05)',
        border: '1px solid rgba(255,255,255,0.18)',
        borderRadius: 8,
        cursor: 'pointer',
        touchAction: 'manipulation',
        WebkitTapHighlightColor: 'transparent',
    };

    return <div style={{display: 'flex', flexDirection: 'column', gap: 8}}>
        {targets.map((t, i) => (
            <button
                key={i}
                onClick={() => fireOne(i)}
                disabled={statuses[i] === 'pending'}
                style={{
                    ...rowBtnStyle,
                    opacity: statuses[i] === 'pending' ? 0.7 : 1,
                    cursor: statuses[i] === 'pending' ? 'wait' : 'pointer',
                }}>
                <span style={{fontSize: 18}}>{icon(statuses[i])}</span>
                <span style={{flex: 1}}>{t.label}</span>
            </button>
        ))}
        <button
            onClick={fireAll}
            style={{
                marginTop: 8,
                width: '100%',
                padding: '16px 20px',
                fontSize: 18,
                fontWeight: 900,
                fontStyle: 'italic',
                letterSpacing: 4,
                textTransform: 'uppercase',
                fontFamily: 'inherit',
                color: 'white',
                background: 'linear-gradient(180deg, #8e1c66 0%, #4a0a4a 100%)',
                border: '1px solid #ffd24a',
                borderRadius: 8,
                boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.25), 0 3px 0 rgba(0,0,0,0.6)',
                cursor: 'pointer',
                touchAction: 'manipulation',
                WebkitTapHighlightColor: 'transparent',
            }}>⚡ Init All</button>
    </div>;
};
